import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { FiDownload } from "react-icons/fi";
import { toast } from "react-toastify";
import CustomButton from "../../components/common/CustomButton";
import {
  useSupportTicketById,
  useAddSupportTicketComment,
  useChangeSupportTicketStatus,
} from "../../custom-hooks/queries";
import { useAuthStore } from "../../store/useAuthStore";
import StatusBadge from "../../components/common/StatusBadge";

const TicketDetailsPage = () => {
  const { ticketId } = useParams();

  const role = useAuthStore((state) => state.role);
  const lowerCaseRole = role?.toLowerCase();

  const [comment, setComment] = useState("");
  const [adminComment, setAdminComment] = useState("");

  const { data, isLoading, isError, refetch } = useSupportTicketById(
    ticketId,
    lowerCaseRole
  );

  const { mutate: addCommentMutation, isPending } =
    useAddSupportTicketComment(lowerCaseRole);

  const { mutate: changeStatusMutation, isPending: isChangingStatus } =
    useChangeSupportTicketStatus();

  const ticket = data?.data;

  /* ---------------- USER COMMENT ---------------- */
  const handleAddComment = () => {
    if (!comment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    addCommentMutation(
      { ticketId, message: comment },
      {
        onSuccess: () => {
          toast.success("Comment added successfully");
          setComment("");
          refetch();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message || "Failed to add comment"
          );
        },
      }
    );
  };

  /* ---------------- ADMIN RESOLVE ---------------- */
  const handleChangeStatus = () => {
    if (!adminComment.trim()) {
      toast.error("Please add a resolution comment");
      return;
    }

    changeStatusMutation(
      {
        ticketId,
        status: "Resolved",
        comment: adminComment,
      },
      {
        onSuccess: () => {
          toast.success("Ticket marked as Resolved");
          setAdminComment("");
          refetch();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message || "Failed to change ticket status"
          );
        },
      }
    );
  };

  /* ---------------- LOADING ---------------- */
  if (isLoading) {
    return (
      <div className="p-10 text-center text-gray-500">
        Loading ticket details...
      </div>
    );
  }

  /* ---------------- ERROR ---------------- */
  if (isError || !ticket) {
    return (
      <div className="p-10 text-center text-red-500">
        Failed to load ticket details
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* HEADER */}
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">
        Ticket Details
      </h1>

      {/* TICKET INFO */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-sm text-gray-500">Ticket ID</p>
            <p className="font-medium text-gray-900">{ticket.ticketCode}</p>
          </div>

          <StatusBadge status={ticket?.status} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm mb-6">
          <div>
            <p className="text-gray-500">Priority</p>
            <p
              className={`font-medium ${
                ticket.priority === "High"
                  ? "text-red-500"
                  : ticket.priority === "Normal"
                  ? "text-yellow-500"
                  : "text-green-500"
              }`}
            >
              {ticket.priority}
            </p>
          </div>

          <div>
            <p className="text-gray-500">Created At</p>
            <p className="font-medium text-gray-900">
              {new Date(ticket.createdAt).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <p className="text-gray-500">Subject</p>
            <p>{ticket.subject}</p>
          </div>

          <div>
            <p className="text-gray-500">Category</p>
            <p>{ticket.category}</p>
          </div>

          <div>
            <p className="text-gray-500">Description</p>
            <p>{ticket.description}</p>
          </div>

          <div>
            <p className="text-gray-500">Attachments</p>
            {ticket.attachments?.length > 0 ? (
              ticket.attachments.map((file, i) => (
                <button
                  key={i}
                  className="flex items-center gap-2 text-[#00C3CC] hover:underline mt-1"
                >
                  <FiDownload />
                  {file.fileName}
                </button>
              ))
            ) : (
              <p className="text-gray-400">No attachments</p>
            )}
          </div>
        </div>
      </div>

      {/* ADMIN RESOLVE SECTION */}
      {lowerCaseRole === "admin" && (
        <div className="bg-white border rounded-xl p-6 mt-6">
          <textarea
            value={adminComment}
            onChange={(e) => setAdminComment(e.target.value)}
            placeholder="Add resolution comment..."
            className="w-full border rounded-lg p-3 text-sm"
            rows={4}
          />

          <div className="flex justify-end mt-3">
            <CustomButton
              onClick={handleChangeStatus}
              disabled={isChangingStatus}
              loading={isChangingStatus}
            >
              {isChangingStatus ? "Changing..." : "Change Status"}
            </CustomButton>
          </div>
        </div>
      )}

      {/* COMMENTS */}
      <div className="bg-white border rounded-xl p-6 mt-6">
        <h3 className="font-semibold text-gray-900 mb-4">Comments</h3>

        {ticket.comments?.length > 0 ? (
          ticket.comments.map((c, i) => (
            <div key={i} className="mb-4">
              <p className="text-sm font-medium text-gray-900">
                {c.authorName}
                {c.isAdmin && (
                  <span className="ml-2 text-xs text-red-500">(Admin)</span>
                )}
                <span className="font-normal ml-2">
                  • {new Date(c.createdAt).toLocaleString()}
                </span>
              </p>
              <p className="text-sm text-gray-700 mt-1">{c.message}</p>
            </div>
          ))
        ) : (
          <p className="text-gray-400 text-sm">No comments yet</p>
        )}

        {/* USER COMMENT */}
        <div className="mt-6">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Write your comment here..."
            className="w-full border rounded-lg p-3 text-sm"
            rows={4}
          />

          <div className="flex justify-end mt-3">
            <CustomButton
              onClick={handleAddComment}
              disabled={isPending}
              loading={isPending}
            >
              {isPending ? "Adding..." : "Add Comment"}
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsPage;
